<template>
  <div>
    <ErrorNotFound v-if="!loading && !agency" data-testid="agency-not-found" />
    <div class="h-1">
      <ProgressBar v-if="loading" data-testid="agency-loading-bar" />
    </div>
    <div v-if="agency">
      <RouterView :agency="agency" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { AgencyConfigPageVisitReportsCachedEnum, useReviewsAgencyByIdQuery } from '@/gql/reviews'
import type { AgencyData } from '@/gql/reviews'
import { mapTranslationsArrToObj } from '@/helpers/translationsHelper'
import { useRoleRedirect } from '@/services/roleRedirect'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys'
import ProgressBar from '@/ui/ProgressBar.vue'
import ErrorNotFound from '@/views/ErrorNotFound.vue'

const YES = 'yes'
const PAGE_VISIT_REPORTS_ENABLED = [AgencyConfigPageVisitReportsCachedEnum.Basic, AgencyConfigPageVisitReportsCachedEnum.Extended]

const props = defineProps<{
  agencyId: string
}>()

useRoleRedirect('manageReviews')

const variables = computed(() => ({ id: props.agencyId }))

const { loading, result, refetch } = useReviewsAgencyByIdQuery(variables, { clientId: 'reviews' })

const agency = computed<AgencyData | undefined>(() => {
  const data = result.value?.manage?.agency
  if (!data) {
    return undefined
  }
  return {
    ...data,
    descriptionTranslationsMap: mapTranslationsArrToObj(data.descriptionTranslations),
    shortDescriptionTranslationsMap: mapTranslationsArrToObj(data.shortDescriptionTranslations),
    linkTextTranslationsMap: mapTranslationsArrToObj(data.linkTextTranslations),
    urlTranslationsMap: mapTranslationsArrToObj(data.urlTranslations),
    ctaSecondaryLabelTranslationsMap: mapTranslationsArrToObj(data.ctaSecondaryLabelTranslations),
    ctaSecondaryUrlTranslationsMap: mapTranslationsArrToObj(data.ctaSecondaryUrlTranslations),
    configFlags: {
      heroActive: data.config.heroImageCached === YES,
      heroEditable: data.config.heroImageAdminCalculated === YES,
      ctaPrimaryActive: data.config.ctaPrimaryCached === YES,
      ctaPrimaryEditable: data.config.ctaPrimaryAdminCalculated === YES,
      ctaSecondaryActive: data.config.ctaSecondaryCached === YES,
      ctaSecondaryEditable: data.config.ctaSecondaryAdminCalculated === YES,
      badgesManagementAllowed: data.config.badgesAdminCalculated === YES,
      widgetsManagementAllowed: data.config.widgetsAdminCalculated === YES,
      socialLinksAllowed: data.config.socialLinksAdminCalculated === YES,
      documentsEditable: data.config.documentsAdminCalculated === YES,
      screenshotsEditable: data.config.portfolioAdminCalculated === YES,
      traitsEditable: data.config.certificatesUspsAdminCalculated === YES,
      descriptionLongEditable: data.config.descriptionLongAdminCalculated === YES,
      pageVisitReportsEnabled: PAGE_VISIT_REPORTS_ENABLED.includes(data.config.pageVisitReportsCached),
    },
  }
})

provide(reloadReviewsAgencyKey, refetch)

const { locale } = useI18n()
watch(locale, () => refetch())
</script>
